import {
  AttachMoneyOutlined,
  CheckroomOutlined,
  Inventory2Outlined,
  LocalOfferOutlined as LocalOfferIcon,
  TrendingDownOutlined,
  TrendingUpOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  GridLegacy as Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AdminEmptyState } from "@/components/common/AdminEmptyState";
import AnalyticsCard from "@/components/common/AnalyticsCard";
import { AppChip } from "@/components/common/AppChip";
import { CardSkeleton } from "@/components/common/CardSkeleton";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import { TableSkeletonRows } from "@/components/common/TableSkeletonRows";
import { tableInCardSx } from "@/components/common/unifiedTableSx";
import { getReportNetworkLoading } from "@/hooks/getReportNetworkLoading";
import { useLocalizedTablePagination } from "@/hooks/useLocalizedTablePagination";
import {
  type PopularProductReportRow,
  usePopularProductsReport,
} from "@/hooks/usePopularProductsReport";
import { downloadCsv } from "@/pages/reports/shared/storeInsightsCsv";
import { StoreInsightsPanel } from "@/pages/reports/shared/StoreInsightsPanel";
import { StoreInsightsReportHeader } from "@/pages/reports/shared/StoreInsightsReportHeader";
import { useStoreInsightsReportFilters } from "@/pages/reports/shared/useStoreInsightsReportFilters";
import {
  type CurrencyCode,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import { splitCategoryNames } from "@/utils/categorySearch";
import { formatInteger, formatPercentFromWhole } from "@/utils/number";
import {
  formatStoreDate,
  formatStoreDateTimeFileStamp,
  getBrowserTimezone,
  getInclusiveStoreDayCount,
  parseStoreDateKey,
} from "@/utils/storeTime";

type SortDirection = "asc" | "desc";
const PRODUCT_PREVIEW_LIMIT = 5;
const RESULTS_ENTER_CLASS = "results-enter";
const RESULTS_ENTER_DURATION_MS = 900;
const POPULAR_PRODUCTS_REPORT_PERIODS = [
  "week_to_date",
  "last_weekend",
  "last_4_weeks",
  "custom",
] as const;

const isAtLeastThreeMonthsBefore = (dateKey: string, comparisonDateKey: string): boolean => {
  const [year, month, day] = comparisonDateKey.split("-").map(Number);
  const comparisonMonthIndex = month - 1 - 3;
  const thresholdYear = year + Math.floor(comparisonMonthIndex / 12);
  const thresholdMonth = ((comparisonMonthIndex % 12) + 12) % 12;
  const finalDayOfThresholdMonth = new Date(
    Date.UTC(thresholdYear, thresholdMonth + 1, 0),
  ).getUTCDate();
  const thresholdDateKey = [
    thresholdYear.toString().padStart(4, "0"),
    (thresholdMonth + 1).toString().padStart(2, "0"),
    Math.min(day, finalDayOfThresholdMonth).toString().padStart(2, "0"),
  ].join("-");

  return dateKey <= thresholdDateKey;
};

type PopularProductsSortField = keyof Pick<
  PopularProductReportRow,
  | "title"
  | "productId"
  | "category"
  | "triedOnCount"
  | "convertedCount"
  | "conversionRate"
  | "convertedRevenue"
>;

interface SortableHeaderCellProps {
  field: PopularProductsSortField;
  label: string;
  sortField: PopularProductsSortField;
  sortDirection: SortDirection;
  onSort: (field: PopularProductsSortField) => void;
  ascendingLabel: string;
  descendingLabel: string;
  align?: "left" | "right";
  sx?: object;
}

interface ConversionListPanelProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  emptyStateIcon: React.ReactNode;
  accentColor: string;
  rows: PopularProductReportRow[];
  formatConversionRate: (value: number) => string;
  formatProductMeta: (row: PopularProductReportRow) => string;
  unknownProductLabel: string;
  emptyLabel: string;
  loading: boolean;
}

interface ConversionRateCellProps {
  value: number;
  formattedValue: string;
}

const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  field,
  label,
  sortField,
  sortDirection,
  onSort,
  ascendingLabel,
  descendingLabel,
  align = "left",
  sx,
}) => {
  const active = sortField === field;

  return (
    <TableCell
      align={align === "right" ? "right" : undefined}
      sortDirection={active ? sortDirection : false}
      sx={sx}
    >
      <TableSortLabel
        active={active}
        direction={active ? sortDirection : "asc"}
        onClick={() => onSort(field)}
        style={align === "right" ? { justifyContent: "flex-end" } : undefined}
      >
        {label}
        {active ? (
          <Box component="span" sx={visuallyHidden}>
            {sortDirection === "desc" ? descendingLabel : ascendingLabel}
          </Box>
        ) : null}
      </TableSortLabel>
    </TableCell>
  );
};

const ConversionListPanel: React.FC<ConversionListPanelProps> = ({
  title,
  subtitle,
  icon,
  emptyStateIcon,
  accentColor,
  rows,
  formatConversionRate,
  formatProductMeta,
  unknownProductLabel,
  emptyLabel,
  loading,
}) => {
  const resultsListRef = React.useRef<HTMLOListElement>(null);
  const wasLoadingRef = React.useRef(loading);

  React.useEffect(() => {
    const resultsList = resultsListRef.current;
    const shouldAnimate = wasLoadingRef.current && !loading && rows.length > 0;
    wasLoadingRef.current = loading;

    if (
      !shouldAnimate ||
      !resultsList ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    resultsList.classList.add(RESULTS_ENTER_CLASS);
    const timeout = window.setTimeout(
      () => resultsList.classList.remove(RESULTS_ENTER_CLASS),
      RESULTS_ENTER_DURATION_MS,
    );

    return () => window.clearTimeout(timeout);
  }, [loading, rows.length]);

  return (
    <StoreInsightsPanel
      title={
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
          <Box component="span" sx={{ color: accentColor, display: "inline-flex" }}>
            {icon}
          </Box>
          <Box component="span">{title}</Box>
          <Box component="span" sx={{ color: "#64748b", fontSize: 12, fontWeight: 400 }}>
            {subtitle}
          </Box>
        </Box>
      }
    >
      <Stack spacing={1.75}>
        {loading ? (
          <>
            <Stack component="ol" spacing={0} sx={{ m: 0, p: 0, listStyle: "none" }}>
              {Array.from({ length: PRODUCT_PREVIEW_LIMIT }).map((_, index) => (
                <Box
                  component="li"
                  key={index}
                  sx={{
                    py: 1.25,
                    borderBottom: "1px solid",
                    borderColor: "rgba(226, 232, 240, 0.8)",
                    "&:last-child": {
                      borderBottom: 0,
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <CardSkeleton width={index % 2 === 0 ? "64%" : "52%"} height={14} />
                      <CardSkeleton width="42%" height={12} sx={{ mt: 0.75 }} />
                    </Box>
                    <CardSkeleton width={44} height={14} />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </>
        ) : rows.length === 0 ? (
          <Box sx={{ py: 2 }}>
            <AdminEmptyState icon={emptyStateIcon} text={emptyLabel} />
          </Box>
        ) : (
          <Stack
            component="ol"
            ref={resultsListRef}
            spacing={0}
            sx={{ m: 0, p: 0, listStyle: "none" }}
          >
            {rows.map((row) => (
              <Box
                component="li"
                key={`${row.rank}-${row.productId ?? "unknown"}`}
                sx={{
                  py: 1.25,
                  borderBottom: "1px solid",
                  borderColor: "rgba(226, 232, 240, 0.8)",
                  "&:last-child": {
                    borderBottom: 0,
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#001F3F",
                        fontWeight: 700,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.title ?? unknownProductLabel}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      {formatProductMeta(row)}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "#001F3F", fontWeight: 700, flex: "0 0 auto" }}
                  >
                    {formatConversionRate(row.conversionRate)}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </StoreInsightsPanel>
  );
};

const ConversionRateCell: React.FC<ConversionRateCellProps> = ({ value, formattedValue }) => {
  const barValue = Math.max(0, Math.min(100, value));

  return (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
      <Typography
        variant="body2"
        component="span"
        sx={{ color: "#001F3F", fontWeight: 700, minWidth: 54, textAlign: "end" }}
      >
        {formattedValue}
      </Typography>
      <Box
        aria-hidden="true"
        sx={{
          width: 96,
          height: 6,
          borderRadius: 999,
          backgroundColor: "#eef2f7",
          overflow: "hidden",
          flex: "0 0 auto",
        }}
      >
        <Box
          sx={{
            width: `${barValue}%`,
            height: "100%",
            borderRadius: 999,
            backgroundColor: "#0b6bff",
          }}
        />
      </Box>
    </Stack>
  );
};

const PopularProductsReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [sortField, setSortField] = React.useState<PopularProductsSortField>("triedOnCount");
  const [sortDirection, setSortDirection] = React.useState<SortDirection>("desc");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const localizedTablePagination = useLocalizedTablePagination();
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const {
    user,
    isCraveUser,
    selectedStores,
    loading,
    period,
    customRange,
    currentDateRange,
    deviceType,
    deviceTypeFilterDisabled,
    customers,
    selectedCustomerIds,
    groups,
    selectedGroups,
    groupCounts,
    filteredStores,
    onCustomerChange,
    onGroupsChange,
    onStoresChange,
    onPeriodChange,
    onCustomRangeChange,
  } = useStoreInsightsReportFilters({
    enableDeviceFilter: false,
    defaultDeviceType: "all",
    defaultPeriod: "week_to_date",
    availablePeriods: POPULAR_PRODUCTS_REPORT_PERIODS,
  });

  const report = usePopularProductsReport(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
  );
  const networkLoading = getReportNetworkLoading(loading, report.loading, report.tableLoading);
  const contentLoading = networkLoading;
  const hasReportData = !contentLoading && !report.error && report.data.rows.length > 0;
  const displayedDayCount = useMemo(
    () => getInclusiveStoreDayCount(currentDateRange.start, currentDateRange.end, browserTimezone),
    [browserTimezone, currentDateRange.end, currentDateRange.start],
  );
  const loadingKey = useMemo(
    () =>
      [
        selectedStores.map((store) => store.id).join(","),
        currentDateRange.start.getTime(),
        currentDateRange.end.getTime(),
        deviceType,
      ].join("|"),
    [currentDateRange.end, currentDateRange.start, deviceType, selectedStores],
  );
  const hasConversionData = report.salesAvailability === "available";
  const tableColumnCount = hasConversionData ? 7 : 4;
  const currentLocale = useMemo(
    () => new Intl.Locale(i18n.resolvedLanguage || i18n.language || "en-US"),
    [i18n.resolvedLanguage, i18n.language],
  );
  const formattedConversionRate = formatPercentFromWhole(
    report.data.conversionRate,
    currentLocale,
    {
      style: "fixed",
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    },
  );
  const [currencyCode, currencySymbol] = useMemo(
    () => getStoreCurrencyIdentifiers(selectedStores),
    [selectedStores],
  );
  const formattedRevenue = formatCurrencyWithNoDecimals(
    report.data.totalRevenue,
    currencyCode as CurrencyCode,
    currencySymbol,
    currentLocale,
  );
  const formatRevenue = (value: number) =>
    formatCurrencyWithNoDecimals(
      value,
      currencyCode as CurrencyCode,
      currencySymbol,
      currentLocale,
    );

  const formatConversionRate = (value: number) =>
    formatPercentFromWhole(value, currentLocale, {
      style: "fixed",
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    });
  const formatProductMeta = (row: PopularProductReportRow) => {
    const count = formatInteger(row.triedOnCount, currentLocale);
    const category = row.category?.trim();

    return category
      ? t("storeInsights.popularProductsReport.text.tryOnsCountWithCategory", {
          category,
          count,
        })
      : t("storeInsights.popularProductsReport.text.tryOnsCount", {
          count,
        });
  };

  const formatReportDateKey = React.useCallback(
    (dateKey: string) => {
      const parsedDate = parseStoreDateKey(dateKey, browserTimezone);
      return parsedDate
        ? formatStoreDate(parsedDate, browserTimezone, currentLocale.baseName)
        : dateKey;
    },
    [browserTimezone, currentLocale.baseName],
  );

  const salesDataNotice = useMemo(() => {
    const salesQueryWindow = report.data.salesQueryWindow;
    if (!salesQueryWindow?.isUnavailable) {
      return null;
    }

    if (salesQueryWindow.requiresCraveContact) {
      return t("storeInsights.popularProductsReport.text.noSalesDataContactCrave");
    }

    if (
      salesQueryWindow.endDate < salesQueryWindow.startDate &&
      isAtLeastThreeMonthsBefore(salesQueryWindow.endDate, salesQueryWindow.startDate)
    ) {
      return t("storeInsights.popularProductsReport.text.salesDataUnavailableContactCrave", {
        endDate: formatReportDateKey(salesQueryWindow.endDate),
      });
    }

    return t("storeInsights.popularProductsReport.text.salesDataUnavailableForRange");
  }, [formatReportDateKey, report.data.salesQueryWindow, t]);

  const limitedSalesDataNotice = useMemo(() => {
    const salesQueryWindow = report.data.salesQueryWindow;
    if (!salesQueryWindow?.isLimited || salesQueryWindow.isUnavailable) {
      return null;
    }

    return t("storeInsights.popularProductsReport.text.limitedSalesDataWindow", {
      startDate: formatReportDateKey(salesQueryWindow.startDate),
      endDate: formatReportDateKey(salesQueryWindow.endDate),
    });
  }, [formatReportDateKey, report.data.salesQueryWindow, t]);

  const loadingLabels = useMemo(() => {
    const loadingKey =
      report.salesAvailability === "unavailable" ? "withoutSalesData" : "withSalesData";
    const labels =
      loadingKey === "withSalesData"
        ? [
            "gatheringTryOns",
            "readingFittingRoomSessions",
            "matchingTryOnsToSalesTransactions",
            "calculatingConversionRate",
            "attributingRevenueToTryOns",
            "rankingProductsByTryOns",
            "puttingResultsTogether",
            "longerDateRanges",
            "thanksForPatience",
          ]
        : [
            "gatheringTryOns",
            "readingFittingRoomSessions",
            "countingTryOns",
            "groupingProductsByCategory",
            "rankingProductsByTryOns",
            "puttingResultsTogether",
            "longerDateRanges",
            "thanksForPatience",
          ];

    return labels.map((label) =>
      t(`storeInsights.popularProductsReport.loading.${loadingKey}.${label}`),
    );
  }, [report.salesAvailability, t]);

  const contextNotice = contentLoading ? (
    <LongRunningDataLoader
      isLoading
      baseLabels={loadingLabels.slice(0, 1)}
      loopingLabels={loadingLabels.slice(1)}
      estimate={{
        meanTimePerStoreMs: 30_000,
        storeCount: selectedStores.length,
        displayedDayCount,
        growth: "logarithmic",
        logarithmicLinearWeight: 0.15,
      }}
      locale={i18n.resolvedLanguage || i18n.language}
      loadingKey={loadingKey}
      estimateLabel={(duration) => t("storeComparison.loading.usuallyUnder", { duration })}
      variant="table"
    />
  ) : salesDataNotice || limitedSalesDataNotice ? (
    <Alert severity="info">{salesDataNotice ?? limitedSalesDataNotice}</Alert>
  ) : null;

  const collator = useMemo(
    () =>
      new Intl.Collator(currentLocale.baseName, {
        numeric: true,
        sensitivity: "base",
      }),
    [currentLocale.baseName],
  );

  const sortedRows = useMemo(() => {
    return report.data.rows.toSorted((left, right) => {
      let comparison = 0;

      if (sortField === "title" || sortField === "productId" || sortField === "category") {
        comparison = collator.compare(left[sortField] ?? "", right[sortField] ?? "");
      } else {
        comparison = left[sortField] - right[sortField];
      }

      if (comparison === 0) {
        comparison = left.rank - right.rank;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [collator, report.data.rows, sortDirection, sortField]);
  const maxPage = Math.max(Math.ceil(sortedRows.length / rowsPerPage) - 1, 0);
  const currentPage = Math.min(page, maxPage);
  const paginatedRows = sortedRows.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage,
  );

  const highestConvertingRows = useMemo(
    () =>
      report.data.rows
        .filter((row) => row.triedOnCount > 0)
        .toSorted((left, right) => {
          const rateComparison = right.conversionRate - left.conversionRate;
          if (rateComparison !== 0) {
            return rateComparison;
          }

          return right.triedOnCount - left.triedOnCount;
        })
        .slice(0, PRODUCT_PREVIEW_LIMIT),
    [report.data.rows],
  );

  const lowestConvertingRows = useMemo(
    () =>
      report.data.rows
        .filter((row) => row.triedOnCount > 0)
        .toSorted((left, right) => {
          const rateComparison = left.conversionRate - right.conversionRate;
          if (rateComparison !== 0) {
            return rateComparison;
          }

          return right.triedOnCount - left.triedOnCount;
        })
        .slice(0, PRODUCT_PREVIEW_LIMIT),
    [report.data.rows],
  );

  const handleRequestSort = (field: PopularProductsSortField) => {
    const isAsc = sortField === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortField(field);
  };

  const handleExport = () => {
    if (sortedRows.length === 0) return;

    downloadCsv(
      [
        [
          t("storeInsights.popularProductsReport.fields.product"),
          t("storeInsights.popularProductsReport.fields.productId"),
          t("storeInsights.popularProductsReport.fields.category"),
          t("storeInsights.popularProductsReport.fields.triedOn"),
          ...(hasConversionData
            ? [
                t("storeInsights.popularProductsReport.fields.converted"),
                t("storeInsights.popularProductsReport.fields.conversionRate"),
                t("storeInsights.popularProductsReport.fields.revenue"),
              ]
            : []),
        ],
        ...sortedRows.map((row) => [
          row.title ?? "",
          row.productId ?? "",
          row.category ?? "",
          row.triedOnCount,
          ...(hasConversionData
            ? [
                row.convertedCount,
                formatConversionRate(row.conversionRate),
                formatRevenue(row.convertedRevenue),
              ]
            : []),
        ]),
      ],
      `popular_products_export_${formatStoreDateTimeFileStamp(new Date(), browserTimezone)}.csv`,
    );
  };

  return (
    <Box sx={{ pb: 4 }}>
      <StoreInsightsReportHeader
        title={t("storeInsights.popularProductsReport.title")}
        icon={<LocalOfferIcon />}
        isCraveUser={isCraveUser}
        currentUserRole={user?.role}
        customers={customers}
        selectedCustomerIds={selectedCustomerIds}
        onCustomerChange={onCustomerChange}
        groups={groups}
        selectedGroups={selectedGroups}
        groupCounts={groupCounts}
        onGroupsChange={onGroupsChange}
        filteredStores={filteredStores}
        selectedStores={selectedStores}
        onStoresChange={onStoresChange}
        loading={loading}
        networkLoading={networkLoading}
        period={period}
        availablePeriods={POPULAR_PRODUCTS_REPORT_PERIODS}
        currentDateRange={currentDateRange}
        customRange={customRange}
        onPeriodChange={onPeriodChange}
        onCustomRangeChange={onCustomRangeChange}
        deviceTypeFilterEnabled
        deviceTypeFilterDisabled={deviceTypeFilterDisabled}
        selectedDeviceType={deviceType}
        notice={contextNotice}
      />

      <Box sx={{ p: 2.5, pt: 0, mt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography component="h2" sx={{ color: "#001F3F", fontSize: 20, fontWeight: 600 }}>
              {t("storeInsights.popularProductsReport.sections.storeActivitySummary")}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} lg={hasConversionData ? 3 : 12}>
            <AnalyticsCard
              icon={<CheckroomOutlined fontSize="small" />}
              title={t("storeInsights.popularProductsReport.cards.totalTryOns")}
              value={hasReportData ? formatInteger(report.data.totalTryOns, currentLocale) : "--"}
              subtitle={t("storeInsights.popularProductsReport.cards.totalTryOnsSubtitle")}
              previousText={null}
              isPositive={false}
              pillText={null}
              showGradient
            />
          </Grid>
          {hasConversionData ? (
            <>
              <Grid item xs={12} md={6} lg={3}>
                <AnalyticsCard
                  icon={<TrendingUpOutlined fontSize="small" />}
                  title={t("storeInsights.popularProductsReport.cards.conversionRate")}
                  value={hasReportData ? formattedConversionRate : "--"}
                  subtitle={t("storeInsights.popularProductsReport.cards.conversionRateSubtitle")}
                  previousText={null}
                  isPositive={true}
                  pillText={null}
                  showGradient
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <AnalyticsCard
                  icon={<Inventory2Outlined fontSize="small" />}
                  title={t("storeInsights.popularProductsReport.cards.unitsSold")}
                  value={
                    hasReportData ? formatInteger(report.data.totalConverted, currentLocale) : "--"
                  }
                  subtitle={t("storeInsights.popularProductsReport.cards.unitsSoldSubtitle")}
                  previousText={null}
                  isPositive={true}
                  pillText={null}
                  showGradient
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <AnalyticsCard
                  icon={<AttachMoneyOutlined fontSize="small" />}
                  title={t("storeInsights.popularProductsReport.cards.tryOnRevenue")}
                  value={hasReportData ? formattedRevenue : "--"}
                  subtitle={t("storeInsights.popularProductsReport.cards.tryOnRevenueSubtitle")}
                  previousText={null}
                  isPositive={true}
                  pillText={null}
                  showGradient
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <ConversionListPanel
                  title={t("storeInsights.popularProductsReport.sections.highestConverting")}
                  subtitle={t("storeInsights.popularProductsReport.text.highestConvertingSubtitle")}
                  icon={<TrendingUpOutlined fontSize="small" />}
                  emptyStateIcon={<TrendingUpOutlined sx={{ fontSize: 40 }} />}
                  accentColor="#16a34a"
                  rows={highestConvertingRows}
                  formatConversionRate={formatConversionRate}
                  formatProductMeta={formatProductMeta}
                  unknownProductLabel={t(
                    "storeInsights.popularProductsReport.placeholders.unknownProduct",
                  )}
                  emptyLabel={t("storeInsights.popularProductsReport.empty.noProducts")}
                  loading={contentLoading}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <ConversionListPanel
                  title={t("storeInsights.popularProductsReport.sections.lowestConverting")}
                  subtitle={t("storeInsights.popularProductsReport.text.lowestConvertingSubtitle")}
                  icon={<TrendingDownOutlined fontSize="small" />}
                  emptyStateIcon={<TrendingDownOutlined sx={{ fontSize: 40 }} />}
                  accentColor="#f97316"
                  rows={lowestConvertingRows}
                  formatConversionRate={formatConversionRate}
                  formatProductMeta={formatProductMeta}
                  unknownProductLabel={t(
                    "storeInsights.popularProductsReport.placeholders.unknownProduct",
                  )}
                  emptyLabel={t("storeInsights.popularProductsReport.empty.noProducts")}
                  loading={contentLoading}
                />
              </Grid>
            </>
          ) : null}

          <Grid item xs={12}>
            <StoreInsightsPanel
              title={t("storeInsights.popularProductsReport.sections.products")}
              onExport={handleExport}
              exportDisabled={report.data.rows.length === 0 || contentLoading}
              headerActions={
                <TablePagination
                  component="div"
                  count={sortedRows.length}
                  page={currentPage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                  onPageChange={(_, nextPage) => setPage(nextPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                  }}
                  sx={{ "& .MuiToolbar-root": { minHeight: 0, px: 0 } }}
                  {...localizedTablePagination}
                />
              }
              footer={
                <TablePagination
                  component="div"
                  count={sortedRows.length}
                  page={currentPage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                  onPageChange={(_, nextPage) => setPage(nextPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                  }}
                  {...localizedTablePagination}
                />
              }
            >
              <TableContainer>
                <Table sx={tableInCardSx}>
                  <TableHead>
                    <TableRow>
                      <SortableHeaderCell
                        field="title"
                        label={t("storeInsights.popularProductsReport.fields.product")}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleRequestSort}
                        ascendingLabel={t("storeInsights.common.sortDirection.ascending")}
                        descendingLabel={t("storeInsights.common.sortDirection.descending")}
                        sx={{ minWidth: 190 }}
                      />
                      <SortableHeaderCell
                        field="productId"
                        label={t("storeInsights.popularProductsReport.fields.productId")}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleRequestSort}
                        ascendingLabel={t("storeInsights.common.sortDirection.ascending")}
                        descendingLabel={t("storeInsights.common.sortDirection.descending")}
                      />
                      <SortableHeaderCell
                        field="category"
                        label={t("storeInsights.popularProductsReport.fields.category")}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleRequestSort}
                        ascendingLabel={t("storeInsights.common.sortDirection.ascending")}
                        descendingLabel={t("storeInsights.common.sortDirection.descending")}
                      />
                      <SortableHeaderCell
                        field="triedOnCount"
                        label={t("storeInsights.popularProductsReport.fields.triedOn")}
                        align="right"
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleRequestSort}
                        ascendingLabel={t("storeInsights.common.sortDirection.ascending")}
                        descendingLabel={t("storeInsights.common.sortDirection.descending")}
                      />
                      {hasConversionData ? (
                        <>
                          <SortableHeaderCell
                            field="convertedCount"
                            label={t("storeInsights.popularProductsReport.fields.converted")}
                            align="right"
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onSort={handleRequestSort}
                            ascendingLabel={t("storeInsights.common.sortDirection.ascending")}
                            descendingLabel={t("storeInsights.common.sortDirection.descending")}
                          />
                          <SortableHeaderCell
                            field="conversionRate"
                            label={t("storeInsights.popularProductsReport.fields.conversionRate")}
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onSort={handleRequestSort}
                            ascendingLabel={t("storeInsights.common.sortDirection.ascending")}
                            descendingLabel={t("storeInsights.common.sortDirection.descending")}
                          />
                          <SortableHeaderCell
                            field="convertedRevenue"
                            label={t("storeInsights.popularProductsReport.fields.revenue")}
                            align="right"
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onSort={handleRequestSort}
                            ascendingLabel={t("storeInsights.common.sortDirection.ascending")}
                            descendingLabel={t("storeInsights.common.sortDirection.descending")}
                          />
                        </>
                      ) : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contentLoading ? (
                      <TableSkeletonRows
                        columnCount={tableColumnCount}
                        rowCount={6}
                        alignments={
                          hasConversionData
                            ? ["left", "left", "left", "right", "right", "left", "right"]
                            : ["left", "left", "left", "right"]
                        }
                      />
                    ) : report.data.rows.length === 0 ? (
                      <TableRow className="table-empty-state">
                        <TableCell colSpan={tableColumnCount} align="center" className="py-8">
                          <AdminEmptyState
                            icon={<Inventory2Outlined sx={{ fontSize: 40 }} />}
                            text={
                              report.error
                                ? t("storeInsights.popularProductsReport.errors.failedLoad")
                                : t("storeInsights.popularProductsReport.empty.noProducts")
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRows.map((row) => (
                        <TableRow key={`${row.rank}-${row.productId ?? "unknown"}`}>
                          <TableCell sx={{ minWidth: 280, verticalAlign: "middle" }}>
                            <div className="text-gray-900" style={{ fontWeight: 500 }}>
                              {row.title ??
                                t(
                                  "storeInsights.popularProductsReport.placeholders.unknownProduct",
                                )}
                            </div>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle" }}>
                            {row.productId ??
                              t("storeInsights.popularProductsReport.placeholders.notAvailable")}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle" }}>
                            {row.category && splitCategoryNames(row.category).length > 0 ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                {splitCategoryNames(row.category).map((category, index) => (
                                  <AppChip
                                    key={`${category}-${index}`}
                                    label={category}
                                    size="small"
                                    tone="category"
                                    sx={{
                                      maxWidth: "100%",
                                      height: "auto",
                                      fontSize: 12,
                                      fontWeight: 600,
                                      "& .MuiChip-label": {
                                        px: 1.25,
                                        py: 0.375,
                                        whiteSpace: "normal",
                                        overflowWrap: "anywhere",
                                      },
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              t("storeInsights.popularProductsReport.placeholders.notAvailable")
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ verticalAlign: "middle" }}>
                            {formatInteger(row.triedOnCount, currentLocale)}
                          </TableCell>
                          {hasConversionData ? (
                            <>
                              <TableCell align="right" sx={{ verticalAlign: "middle" }}>
                                {formatInteger(row.convertedCount, currentLocale)}
                              </TableCell>
                              <TableCell sx={{ verticalAlign: "middle", minWidth: 190 }}>
                                <ConversionRateCell
                                  value={row.conversionRate}
                                  formattedValue={formatConversionRate(row.conversionRate)}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ verticalAlign: "middle" }}>
                                <Typography
                                  variant="body2"
                                  component="span"
                                  sx={{ color: "#001F3F", fontWeight: 700 }}
                                >
                                  {formatRevenue(row.convertedRevenue)}
                                </Typography>
                              </TableCell>
                            </>
                          ) : null}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </StoreInsightsPanel>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default PopularProductsReport;
